import * as React from 'react';
import px from 'prop-types';
import cx from 'classnames';
import { v4 } from 'uuid';
import { useTranslation } from 'Common/hooks';

export default function ToolTip({
    className,
    selector,
    tip,
    translationParams = null,
    children,
    disabled = false,
    forceShowTip = null,
    placement = 'top',
    trigger = 'click',
    cursor = 'default',
    ...props
}) {
    const id = React.useRef(`tip-${v4()}`);
    const translatedTip = useTranslation(tip, translationParams) || '';
    const tooltipRef = React.useRef(null);
    const bsTooltipRef = React.useRef(null);
    const onClick = React.useMemo(
        () =>
            disabled || trigger !== 'click'
                ? null
                : (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                  },
        [disabled, trigger]
    );

    React.useEffect(() => {
        const el = selector ? document.querySelector(selector) : tooltipRef.current;

        if (el && typeof window !== 'undefined' && window.bootstrap) {
            // Create tooltip instance
            bsTooltipRef.current = new window.bootstrap.Tooltip(el, {
                placement,
                trigger,
                title: disabled && !forceShowTip ? '' : translatedTip,
            });

            // Add event listeners for shown and hidden events
            const onShown = () => {
                const tooltipElement = document.querySelector('.tooltip');

                if (tooltipElement && className) {
                    tooltipElement.classList.add(className);
                }
            };

            const onHidden = () => {
                const tooltipElement = document.querySelector('.tooltip');

                if (tooltipElement && className) {
                    tooltipElement.classList.remove(className);
                }
            };

            el.addEventListener('shown.bs.tooltip', onShown);
            el.addEventListener('hidden.bs.tooltip', onHidden);

            return () => {
                if (bsTooltipRef.current) {
                    el.removeEventListener('shown.bs.tooltip', onShown);
                    el.removeEventListener('hidden.bs.tooltip', onHidden);
                    bsTooltipRef.current.dispose();
                    bsTooltipRef.current = null;
                }
            };
        }
    }, [className, selector, placement, trigger, translatedTip, disabled, forceShowTip]);

    React.useEffect(() => {
        const el = selector ? document.querySelector(selector) : tooltipRef.current;

        if (el && bsTooltipRef.current) {
            const title = disabled && !forceShowTip ? '' : translatedTip;

            // Update title attributes
            el.setAttribute('title', title);
            el.setAttribute('data-bs-original-title', title);

            // Update tooltip instance
            bsTooltipRef.current.setContent({
                '.tooltip-inner': title,
            });

            // Enable/disable tooltip
            if (disabled && !forceShowTip) {
                bsTooltipRef.current.disable();
            } else {
                bsTooltipRef.current.enable();
            }

            // Force show/hide if specified
            if (typeof forceShowTip === 'boolean') {
                if (forceShowTip) {
                    bsTooltipRef.current.show();
                } else {
                    bsTooltipRef.current.hide();
                }
            }
        }
    }, [disabled, forceShowTip, selector, translatedTip]);

    // Cleanup on unmount
    React.useEffect(() => {
        return () => {
            if (bsTooltipRef.current) {
                bsTooltipRef.current.dispose();
            }
        };
    }, []);

    return (
        <div
            ref={tooltipRef}
            id={id.current}
            className={cx('ToolTip', className)}
            style={{ cursor }}
            role="button"
            data-bs-toggle="tooltip"
            data-bs-placement={placement}
            onClick={onClick}
            title={disabled && !forceShowTip ? '' : translatedTip}
            data-bs-original-title={disabled && !forceShowTip ? '' : translatedTip}
            {...props}
        >
            {children}
        </div>
    );
}

ToolTip.propTypes = {
    className: px.string,
    selector: px.string,
    tip: px.node,
    children: px.node,
    disabled: px.bool,
    cursor: px.string,
    forceShowTip: px.bool,
    translationParams: px.objectOf(px.any),
    placement: px.string,
    trigger: px.string,
};
